import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { UserPlus, UserSearch, Handshake } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preRole = params.get("role") === "employer" ? "employer" : "candidate";

  const [role, setRole] = useState(preRole);
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role,
        company_name: role === "employer" ? form.company_name : undefined,
        phone: form.phone || undefined,
      });
      toast.success(`Welcome, ${u.name}!`);
      const dash = u.role === "employer" ? "/dashboard/employer" : "/dashboard/candidate";
      nav(dash);
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] container-ax py-14 md:py-20 grid md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-5">
        <div className="eyebrow mb-2">Create an account</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-navy">Join Recruvo.</h1>
        <p className="mt-4 text-slate-600 max-w-md">Whether you're hiring or job hunting — you're two minutes away from getting started.</p>
      </div>

      <form onSubmit={submit} className="md:col-span-7 border border-slate-200 rounded-2xl p-6 md:p-8 bg-white space-y-5 shadow-sm">
        <div>
          <div className="text-xs font-medium text-slate-600 mb-2">I am a…</div>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setRole("candidate")}
              data-testid={TID.registerRoleCandidate}
              className={`rounded-xl border p-4 text-left transition-colors ${role === "candidate" ? "border-gold bg-gold/5" : "border-slate-200 hover:border-slate-300"}`}>
              <UserSearch className="w-5 h-5 text-navy" />
              <div className="mt-2 font-display font-semibold text-navy">Job Seeker</div>
              <div className="text-xs text-slate-500 mt-0.5">Find your next role, free forever.</div>
            </button>
            <button type="button" onClick={() => setRole("employer")}
              data-testid={TID.registerRoleEmployer}
              className={`rounded-xl border p-4 text-left transition-colors ${role === "employer" ? "border-gold bg-gold/5" : "border-slate-200 hover:border-slate-300"}`}>
              <Handshake className="w-5 h-5 text-navy" />
              <div className="mt-2 font-display font-semibold text-navy">Employer</div>
              <div className="text-xs text-slate-500 mt-0.5">Hire vetted candidates fast.</div>
            </button>
          </div>
        </div>

        <GoogleSignInButton preferredRole={role} label={`Sign up with Google as ${role === "employer" ? "Employer" : "Job Seeker"}`} />
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <div className="text-[11px] uppercase tracking-widest text-slate-400">or with email</div>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600">Full name</label>
          <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            data-testid={TID.registerName}
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </div>

        {role === "employer" && (
          <div>
            <label className="text-xs font-medium text-slate-600">Company name</label>
            <input required={role === "employer"} value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
              data-testid={TID.registerCompany}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-slate-600">Email</label>
          <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
            data-testid={TID.registerEmail}
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Password</label>
          <input required type="password" minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})}
            data-testid={TID.registerPassword}
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
          <div className="text-[11px] text-slate-500 mt-1">At least 6 characters</div>
        </div>

        <button disabled={busy} type="submit" data-testid={TID.registerSubmit} className="btn-navy w-full justify-center disabled:opacity-60">
          <UserPlus className="w-4 h-4" /> {busy ? "Creating account…" : "Create account"}
        </button>

        <div className="text-sm text-slate-500 text-center">
          Already have an account? <Link to="/login" className="text-navy font-medium hover:text-gold">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
